import { useState } from "react";
import {
  MAX_REASON_TEXT_LENGTH,
  REASON_CODES_BY_DIRECTION,
  type FeedbackTally,
  type FeedbackVote,
  type ReasonCode,
  type SubPickerReasonCode,
  type VoteDirection,
} from "@skills-marketplace/contracts/contract-8-feedback";
import {
  FEEDBACK_COPY,
  isSubPickerCode,
  reasonDetailLabel,
  reasonLabel,
  subPickerOptions,
  usefulnessDisplay,
  usefulnessHeadline,
  voteConfirmation,
} from "../lib/feedback";
import ThumbUpIcon from "../icons/ThumbUpIcon";
import ThumbDownIcon from "../icons/ThumbDownIcon";
import styles from "./VoteBlock.module.css";

interface Props {
  /** Public tally for this entry, or undefined when it has no votes. */
  tally: FeedbackTally | undefined;

  /** The signed-in user's own vote on this entry, or null if they haven't voted. */
  myVote: FeedbackVote | null;

  /**
   * Commit a vote together with its reasons. Called EXACTLY ONCE per commit, from
   * Submit or Skip — never from a thumb click. One call, one write: this is where
   * #617's "the vote and its reason commit together in a single state update"
   * lands on the client. Splitting this into a vote call plus a reason call would
   * reintroduce precisely the drift the AC forbids.
   */
  onVote: (
    direction: VoteDirection,
    reasonCodes: ReasonCode[],
    reasonText: string | null,
    reasonDetails?: Partial<Record<ReasonCode, string>>,
  ) => void;

  /** Withdraw the user's vote and its reasons together (Undo). */
  onWithdraw: () => void;
}

/**
 * The detail-page vote UI — "Was this skill useful?" with deferred reason capture
 * (issues #617 and #618).
 *
 * Interaction design ported from the validated prototype. The load-bearing part is
 * the DEFERRED COMMIT: clicking a thumb is a local selection that highlights the
 * choice and reveals the "Why?" step, and changes nothing public. The vote is only
 * written when the voter picks Submit or Skip, which is what makes the count and
 * the contributor's copy of the reason inseparable.
 *
 * Errors are the caller's to surface. This component reports intent and reflects
 * the props it is given, so a failed write simply leaves the props unchanged.
 */
export default function VoteBlock({ tally, myVote, onVote, onWithdraw }: Props) {
  // Pre-commit selection. Local by AC: "selecting a thumb is a local-only
  // selection ... without changing the public count", so none of this is lifted.
  const [pendingDirection, setPendingDirection] = useState<VoteDirection | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<ReasonCode[]>([]);
  // Second-level detail tags (#782), keyed by the parent chip they were given
  // under. Keys here are always a subset of selectedCodes — deselecting a
  // chip drops its entry too, so a tag never outlives the selection it was
  // attributed to.
  const [selectedDetails, setSelectedDetails] = useState<Partial<Record<ReasonCode, string>>>({});
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");

  const display = usefulnessDisplay(tally);

  function resetPending() {
    setPendingDirection(null);
    setSelectedCodes([]);
    setSelectedDetails({});
    setOtherOpen(false);
    setOtherText("");
  }

  function selectThumb(direction: VoteDirection) {
    // Switching direction before committing drops any chips already checked and
    // any half-typed "Other" text: the down-vote chips aren't valid choices once
    // you're back on the up side, and vice versa.
    if (direction !== pendingDirection) {
      setSelectedCodes([]);
      setSelectedDetails({});
      setOtherOpen(false);
      setOtherText("");
    }
    setPendingDirection(direction);
  }

  function toggleCode(code: ReasonCode) {
    const wasSelected = selectedCodes.includes(code);
    setSelectedCodes((current) =>
      wasSelected ? current.filter((c) => c !== code) : [...current, code],
    );
    // Deselecting a chip drops its sub-picker tag too — never leave an
    // orphaned detail attributed to a chip that's no longer chosen.
    if (wasSelected) {
      setSelectedDetails((current) => {
        if (!(code in current)) return current;
        const next = { ...current };
        delete next[code];
        return next;
      });
    }
  }

  function selectDetail(code: ReasonCode, value: string) {
    // Tapping a different option replaces the prior selection (AC); tapping
    // the same option again clears it, mirroring the top-level chips' own
    // toggle behaviour and giving a way to undo without deselecting the chip.
    setSelectedDetails((current) => {
      if (current[code] === value) {
        const next = { ...current };
        delete next[code];
        return next;
      }
      return { ...current, [code]: value };
    });
  }

  function commit(
    reasonCodes: ReasonCode[],
    reasonText: string | null,
    reasonDetails?: Partial<Record<ReasonCode, string>>,
  ) {
    if (!pendingDirection) return;
    const details = reasonDetails && Object.keys(reasonDetails).length > 0 ? reasonDetails : undefined;
    // Omit the 4th argument entirely when there's nothing to carry, rather than
    // passing an explicit undefined — keeps every existing 3-arg call site (and
    // its tests) meaning exactly what it always meant.
    if (details) {
      onVote(pendingDirection, reasonCodes, reasonText, details);
    } else {
      onVote(pendingDirection, reasonCodes, reasonText);
    }
    resetPending();
  }

  function submit() {
    const trimmed = otherOpen ? otherText.trim().slice(0, MAX_REASON_TEXT_LENGTH) : "";
    // Submitting with nothing checked and no text is deliberately identical to
    // Skip — the voter took an action but supplied no reason, so none is recorded.
    commit(selectedCodes, trimmed || null, selectedDetails);
  }

  return (
    <section className={styles.block} aria-labelledby="usefulness-heading">
      <div className={styles.summary}>
        <ThumbUpIcon className={styles.summaryIcon} />
        <div>
          <div id="usefulness-heading" className={styles.summaryHeadline}>
            {usefulnessHeadline(display)}
          </div>
          {/*
            The raw count appears only as a sub-line, and only when there is one to
            report. At zero votes "0 votes so far" adds nothing but noise, so the
            headline stands alone.
          */}
          {display.kind === "insufficient" && (
            <div className={styles.summaryDetail}>
              {display.total} {display.total === 1 ? "vote" : "votes"} so far
            </div>
          )}
        </div>
      </div>

      {myVote ? (
        <div className={styles.thanks}>
          {/* ONE headline, whether or not a reason was given. FEEDBACK_COPY carried a
              `thanksWithReason` variant ("shared privately with the contributor") that
              nothing rendered; wiring it in turned out to repeat a promise this block
              ALREADY makes permanently in `privacyNote` below, so the same sentence
              appeared twice. The unused string is deleted rather than left as a tempting
              near-duplicate (#571 review).*/}
          <div className={styles.thanksHeadline}>{FEEDBACK_COPY.thanks}</div>
          <div className={styles.thanksDetail}>
            {voteConfirmation(myVote.direction)} {FEEDBACK_COPY.countedAnonymously}
          </div>
          <button type="button" className={styles.undoButton} onClick={onWithdraw}>
            {FEEDBACK_COPY.undo}
          </button>
          {(myVote.reasonCodes.length > 0 || myVote.reasonText) && (
            <p className={styles.reasonEcho}>
              You told the contributor:{" "}
              {myVote.reasonCodes
                .map((code) => {
                  const detail = myVote.reasonDetails?.[code];
                  return detail ? `${reasonLabel(code)} (${reasonDetailLabel(detail)})` : reasonLabel(code);
                })
                .join(", ")}
              {myVote.reasonCodes.length > 0 && myVote.reasonText ? " — " : ""}
              {myVote.reasonText ? `"${myVote.reasonText}"` : ""}
            </p>
          )}
        </div>
      ) : (
        <>
          <div className={styles.prompt}>
            <div className={styles.promptHeading}>{FEEDBACK_COPY.votePrompt}</div>
            <div className={styles.thumbs}>
              <button
                type="button"
                className={pendingDirection === "up" ? styles.thumbSelected : styles.thumb}
                aria-pressed={pendingDirection === "up"}
                onClick={() => selectThumb("up")}
              >
                <ThumbUpIcon className={styles.thumbIcon} />
                {FEEDBACK_COPY.voteUseful}
              </button>
              <button
                type="button"
                className={pendingDirection === "down" ? styles.thumbSelected : styles.thumb}
                aria-pressed={pendingDirection === "down"}
                onClick={() => selectThumb("down")}
              >
                <ThumbDownIcon className={styles.thumbIcon} />
                {FEEDBACK_COPY.voteNotUseful}
              </button>
            </div>
          </div>

          {pendingDirection && (
            <div className={styles.reasonStep}>
              <div className={styles.reasonHeading}>
                {FEEDBACK_COPY.reasonHeading}{" "}
                <span className={styles.reasonHint}>{FEEDBACK_COPY.reasonHint}</span>
              </div>

              {/*
                Multi-select, per #618. Rendered as toggle buttons with aria-pressed
                rather than checkbox inputs: the AC's "render as checkboxes" is about
                SEMANTICS — any combination applies, unlike radios — and a toggle
                button conveys that while keeping the prototype's chip styling and
                native button keyboard handling.
              */}
              <div className={styles.chips}>
                {REASON_CODES_BY_DIRECTION[pendingDirection].map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={selectedCodes.includes(code) ? styles.chipSelected : styles.chip}
                    aria-pressed={selectedCodes.includes(code)}
                    onClick={() => toggleCode(code)}
                  >
                    {reasonLabel(code)}
                  </button>
                ))}
                <button
                  type="button"
                  className={otherOpen ? styles.chipSelected : styles.chip}
                  aria-pressed={otherOpen}
                  onClick={() => setOtherOpen((open) => !open)}
                >
                  {FEEDBACK_COPY.reasonOther}
                </button>
              </div>

              {/*
                #782: a second-level, single-select, tap-only sub-picker under
                each selected chip that offers one. Stacked in the same order as
                the chip row above, one per eligible chip that's currently
                checked — never blocking Submit/Skip, never defaulted.
              */}
              {REASON_CODES_BY_DIRECTION[pendingDirection]
                .filter(
                  (code): code is SubPickerReasonCode =>
                    selectedCodes.includes(code) && isSubPickerCode(code),
                )
                .map((code) => (
                  <div key={code} className={styles.subPicker}>
                    <div className={styles.subPickerHeading}>
                      {FEEDBACK_COPY.subPickerHeading(reasonLabel(code))}{" "}
                      <span className={styles.subPickerHint}>{FEEDBACK_COPY.subPickerHint}</span>
                    </div>
                    <div className={styles.chips}>
                      {subPickerOptions(code).map((value) => (
                        <button
                          key={value}
                          type="button"
                          className={selectedDetails[code] === value ? styles.subChipSelected : styles.subChip}
                          aria-pressed={selectedDetails[code] === value}
                          onClick={() => selectDetail(code, value)}
                        >
                          {reasonDetailLabel(value)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}

              {otherOpen && (
                <input
                  type="text"
                  className={styles.otherInput}
                  // A courtesy that stops typing into a wall. The real limit is
                  // enforced server-side — see MAX_REASON_TEXT_LENGTH in contract 8.
                  maxLength={MAX_REASON_TEXT_LENGTH}
                  placeholder={FEEDBACK_COPY.reasonOtherPlaceholder}
                  aria-label={FEEDBACK_COPY.reasonOtherPlaceholder}
                  value={otherText}
                  onChange={(event) => setOtherText(event.target.value)}
                />
              )}

              {/*
                Skip sits beside Submit at the same size and weight, per #618 — not a
                smaller text link — so declining to give a reason costs the voter
                nothing. Both commit the vote; they differ only in whether a reason
                rides along.
              */}
              <div className={styles.actions}>
                <button type="button" className={styles.submitButton} onClick={submit}>
                  {FEEDBACK_COPY.submit}
                </button>
                <button type="button" className={styles.skipButton} onClick={() => commit([], null)}>
                  {FEEDBACK_COPY.skip}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <p className={styles.privacyNote}>{FEEDBACK_COPY.privacyNote}</p>
    </section>
  );
}
